/**
 * API base URL resolution (login, workspace, analyses, companies, etc.).
 *
 * **After production build:** edit `runtime-config.json` next to `index.html` (same folder as static assets).
 * Set `apiBaseUrl` to your API root, e.g. `"https://api.example.com"` (no trailing slash). Empty string keeps
 * build-time values below.
 *
 * **Build time:** optional `REACT_APP_API_URL` in `.env` (used when runtime file is absent or `apiBaseUrl` is empty).
 * If neither is set, defaults to local API HTTPS (`https://localhost:7051` — see `api/PctIq.Api/Properties/launchSettings.json` profile `https`).
 *
 * **Paths:** all `ENDPOINTS` values are path+query only (start with `/`); combine with `BASE_URL` for full URLs.
 */

const API_V1 = '/api/v1';
const PCT_V1 = '/api/pct/v1';

/** If omitted, `http://` is prepended so host-only values work in browsers. */
function ensureHttpOrHttpsBase(raw: string): string {
  const t = raw.trim().replace(/\/$/, '');
  if (t === '') return t;
  if (/^https?:\/\//i.test(t)) return t;
  return `http://${t.replace(/^\/*/, '')}`;
}

/** Default when no env and no runtime override: matches Kestrel HTTPS URL in launchSettings (`https` profile). */
const DEFAULT_LOCAL_API_BASE = 'https://localhost:7051';

function buildTimeApiBaseUrl(): string {
  const v = process.env.REACT_APP_API_URL;
  if (v != null && v.trim() !== '') {
    return ensureHttpOrHttpsBase(v);
  }
  return DEFAULT_LOCAL_API_BASE;
}

export const API_CONFIG = {
  BASE_URL: buildTimeApiBaseUrl(),
  ENDPOINTS: {
    // ─── Auth (`/api/v1`) ─────────────────────────────────────────────
    AUTHENTICATE: `${API_V1}/Auth/login`,
    AUTH_CHANGE_PASSWORD: `${API_V1}/Auth/change-password`,
    AUTH_PASSWORD_RESET_COMPLETE: `${API_V1}/Auth/password-reset/complete`,

    // ─── Client workspace (`/api/pct/v1/workspace`) ─────────────────
    WORKSPACE_COMPANY: `${PCT_V1}/workspace/company`,
    WORKSPACE_LAWFIRM: `${PCT_V1}/workspace/lawfirm`,
    WORKSPACE_LAWFIRM_USERS: `${PCT_V1}/workspace/lawfirm/users`,
    WORKSPACE_LAWFIRM_USERS_UPDATE: `${PCT_V1}/workspace/lawfirm/users/update`,
    WORKSPACE_LAWFIRM_USERS_RESET_PASSWORD: `${PCT_V1}/workspace/lawfirm/users/reset-password`,

    // ─── Platform (Root) users — admin only ────────────────────────
    PLATFORM_ROOT_USERS: `${PCT_V1}/platform/users`,
    PLATFORM_ROOT_USERS_UPDATE: `${PCT_V1}/platform/users/update`,
    PLATFORM_ROOT_USERS_RESET_PASSWORD: `${PCT_V1}/platform/users/reset-password`,

    // ─── Law firms (admin / licenses) ──────────────────────────────
    LAWFIRMS: `${PCT_V1}/lawfirms`,
    lawFirm: (id: string) => `${PCT_V1}/lawfirms/${encodeURIComponent(id)}`,
    lawFirmAdminPasswordReset: (id: string) => `${PCT_V1}/lawfirms/${encodeURIComponent(id)}/admin/password-reset`,
    lawFirmActive: (id: string) => `${PCT_V1}/lawfirms/${encodeURIComponent(id)}/active`,

    // ─── Firm → companies & company users ───────────────────────────
    lawFirmCompanies: (lawFirmId: string) => `${PCT_V1}/lawfirms/${encodeURIComponent(lawFirmId)}/companies`,
    lawFirmCompany: (lawFirmId: string, companyId: string) =>
      `${PCT_V1}/lawfirms/${encodeURIComponent(lawFirmId)}/companies/${encodeURIComponent(companyId)}`,
    lawFirmCompanyAnalyses: (lawFirmId: string, companyId: string) =>
      `${PCT_V1}/lawfirms/${encodeURIComponent(lawFirmId)}/companies/${encodeURIComponent(companyId)}/analyses`,
    lawFirmCompanyUsers: (lawFirmId: string, companyId: string) =>
      `${PCT_V1}/lawfirms/${encodeURIComponent(lawFirmId)}/companies/${encodeURIComponent(companyId)}/users`,
    lawFirmCompanyUsersUpdate: (lawFirmId: string, companyId: string) =>
      `${PCT_V1}/lawfirms/${encodeURIComponent(lawFirmId)}/companies/${encodeURIComponent(companyId)}/users/update`,
    lawFirmCompanyUsersResetPassword: (lawFirmId: string, companyId: string) =>
      `${PCT_V1}/lawfirms/${encodeURIComponent(lawFirmId)}/companies/${encodeURIComponent(companyId)}/users/reset-password`,

    // ─── Analyses (client workspace) ───────────────────────────────
    ANALYSES: `${PCT_V1}/analyses`,
    ANALYSES_DELETED: `${PCT_V1}/analyses/deleted`,
    analysis: (id: string) => `${PCT_V1}/analyses/${encodeURIComponent(id)}`,
    analysisCopy: (id: string) => `${PCT_V1}/analyses/${encodeURIComponent(id)}/copy`,
    analysisIndividuals: (id: string) => `${PCT_V1}/analyses/${encodeURIComponent(id)}/individuals`,
    analysisCompensation: (id: string) => `${PCT_V1}/analyses/${encodeURIComponent(id)}/compensation`,
    analysisSeverance: (id: string) => `${PCT_V1}/analyses/${encodeURIComponent(id)}/severance`,
    analysisParachuteSection: (id: string, sectionSlug: string) =>
      `${PCT_V1}/analyses/${encodeURIComponent(id)}/parachute/${encodeURIComponent(sectionSlug)}`,
    analysisBaseAmount: (id: string) => `${PCT_V1}/analyses/${encodeURIComponent(id)}/base-amount`,
    analysisTransaction: (id: string) => `${PCT_V1}/analyses/${encodeURIComponent(id)}/transaction`,
    analysisRestore: (id: string) => `${PCT_V1}/analyses/${encodeURIComponent(id)}/restore`,
    analysisRuns: (id: string) => `${PCT_V1}/analyses/${encodeURIComponent(id)}/runs`,
    analysisRunsLast: (id: string) => `${PCT_V1}/analyses/${encodeURIComponent(id)}/runs/last`,
    analysisRunsHistory: (id: string, limit: number) =>
      `${PCT_V1}/analyses/${encodeURIComponent(id)}/runs/history?limit=${encodeURIComponent(String(limit))}`,
    analysisRunSnapshot: (analysisId: string, runId: string) =>
      `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/runs/${encodeURIComponent(runId)}/snapshot`,

    // ─── Documents (per-analysis, run-scoped artifacts) ─────────────────
    analysisDocuments: (analysisId: string, runId?: string, latestOnly = true) => {
      const params = new URLSearchParams();
      if (runId) params.set('runId', runId);
      if (latestOnly) params.set('latestOnly', 'true');
      const qs = params.toString();
      return `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/documents${qs ? `?${qs}` : ''}`;
    },
    analysisRunDocuments: (analysisId: string, runId: string) =>
      `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/runs/${encodeURIComponent(runId)}/documents`,
    analysisRunDocumentsGenerateAll: (analysisId: string, runId: string) =>
      `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/runs/${encodeURIComponent(runId)}/documents/generate-all`,
    documentDownload: (documentId: string) => `${PCT_V1}/documents/${encodeURIComponent(documentId)}/download`,

    // ─── Shareholders (per-analysis cap table) ──────────────────────────
    analysisShareholders: (analysisId: string) => `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/shareholders`,
    analysisShareholdersBulkUpsert: (analysisId: string) =>
      `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/shareholders/bulk-upsert`,
    analysisShareholder: (analysisId: string, shareholderId: string) =>
      `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/shareholders/${encodeURIComponent(shareholderId)}`,
    analysisVoteRoomLaunch: (analysisId: string) =>
      `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/vote-room/launch`,
    analysisVoteRoomShutdown: (analysisId: string) =>
      `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/vote-room/shutdown`,
    analysisVoteRoomPrelaunchEmailVerification: (analysisId: string) =>
      `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/vote-room/prelaunch/email-verification`,
    analysisVoteRoomWaiverReadiness: (analysisId: string) =>
      `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/vote-room/waiver-readiness`,
    voteRoomPrecheckConfirmEmail: `${PCT_V1}/vote-room/prelaunch/confirm-email`,

    // ─── Approval envelopes (DocuSign + Internal) ───────────────────────
    documentSendForSignature: (documentId: string) =>
      `${PCT_V1}/documents/${encodeURIComponent(documentId)}/send-for-signature`,
    analysisEnvelopes: (analysisId: string) => `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/envelopes`,
    envelope: (envelopeId: string) => `${PCT_V1}/envelopes/${encodeURIComponent(envelopeId)}`,
    envelopeVoid: (envelopeId: string) => `${PCT_V1}/envelopes/${encodeURIComponent(envelopeId)}/void`,
    analysisManualVote: (analysisId: string) => `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/votes/manual`,
    analysisRunTally: (analysisId: string, runId: string) =>
      `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/runs/${encodeURIComponent(runId)}/tally`,
    analysisRunAuditExport: (analysisId: string, runId: string) =>
      `${PCT_V1}/analyses/${encodeURIComponent(analysisId)}/runs/${encodeURIComponent(runId)}/audit-export`,
  },
};

type RuntimeConfigJson = {
  /** Backend origin only, e.g. `https://api.example.com` or `http://10.0.0.5:8080` */
  apiBaseUrl?: string;
};

/**
 * Loads `runtime-config.json` from the deployed static root and overrides `API_CONFIG.BASE_URL` when
 * `apiBaseUrl` is non-empty. Call once before rendering the app (see `index.tsx`).
 */
export async function loadRuntimeConfig(): Promise<void> {
  const base = (process.env.PUBLIC_URL ?? '').replace(/\/$/, '');
  const url = `${base}/runtime-config.json`;
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return;
    const data = (await res.json()) as RuntimeConfigJson;
    const raw = data.apiBaseUrl?.trim();
    if (raw) {
      API_CONFIG.BASE_URL = ensureHttpOrHttpsBase(raw);
    }
  } catch {
    // Missing file, invalid JSON, or offline — keep build-time `API_CONFIG`.
  }
}
